import { Reveal } from "./Reveal";
import { SectionHead } from "./SectionHead";

const list = (v) => (Array.isArray(v) ? v : []);

export const CampaignMetrics = ({ c }) => (
  <section className="relative z-10 -mt-10" data-testid="cs-top-metrics">
    <div className={`mx-auto grid max-w-7xl gap-3 px-5 sm:px-8 ${list(c.metrics).length === 3 ? "sm:grid-cols-3" : "grid-cols-2 lg:grid-cols-4"}`}>
      {list(c.metrics).map((m, i) => (
        <Reveal key={m.label} y={12} className="rounded-[24px] border border-violet-100 bg-white p-5 shadow-[0_30px_60px_-40px_rgba(46,16,101,.6)] sm:p-7">
          <p className="text-3xl font-extrabold tracking-tight text-[#581C87] sm:text-5xl" data-testid={`cs-metric-value-${i}`}>{m.value}</p>
          <p className="mt-2 text-sm font-semibold text-[#13052A]" data-testid={`cs-metric-label-${i}`}>{m.label}</p>
          {m.tag && <p className="mt-1 text-xs font-semibold text-slate-600">{m.tag}</p>}
        </Reveal>
      ))}
    </div>
  </section>
);

export const CampaignIntro = ({ c }) => (
  <section className="bg-white pb-6 pt-20 sm:pt-24" data-testid="cs-intro">
    <Reveal className="mx-auto max-w-4xl space-y-4 px-5 sm:px-8">
      {list(c.hero_paras).map((p) => <p key={p} className="text-base leading-relaxed text-slate-600 md:text-lg">{p}</p>)}
    </Reveal>
  </section>
);

const Block = ({ title, paras }) => (
  <div>
    <h3 className="text-lg font-extrabold text-[#13052A] sm:text-xl">{title}</h3>
    <div className="mt-3 space-y-3 text-base leading-relaxed text-slate-600">{list(paras).map((p) => <p key={p}>{p}</p>)}</div>
  </div>
);

const Creative = ({ img, featured }) => (
  <img src={img.src} alt={img.alt} width={img.w} height={img.h} loading="lazy"
    className={`h-auto w-full rounded-2xl bg-white shadow-[0_24px_50px_-30px_rgba(19,5,42,.5)] ring-1 ring-violet-100 ${featured ? "max-w-[360px]" : "max-w-[240px]"}`} />
);

const Spotlight = ({ cp }) => (
  <div className="mt-14 grid items-start gap-10 lg:grid-cols-[1fr_1.1fr]" data-testid={`cs-creative-${cp.id}`}>
    <figure className="flex flex-col items-start">
      <Creative img={cp.spotlight} featured />
      <figcaption className="mt-4 max-w-[360px]">
        <span className="block text-base font-extrabold text-[#13052A]" data-testid="cs-creative-caption">{cp.spotlight.caption}</span>
        <span className="mt-1 block text-sm text-slate-600">{cp.spotlight.note}</span>
      </figcaption>
    </figure>
    <div>
      <p className="eyebrow text-[#7C3AED]"><span className="h-1.5 w-1.5 rounded-full bg-[#2563EB]" />Creative spotlight</p>
      <h3 className="mt-4 text-2xl font-extrabold text-[#13052A] sm:text-3xl">The genuine adverts behind the results</h3>
      <p className="mt-3 text-base leading-relaxed text-slate-600">Supporting creatives from the same 50% Off campaign, shown as they appeared on Instagram.</p>
      <div className="mt-6 grid grid-cols-2 gap-4">{list(cp.creatives).map((img) => <Creative key={img.src} img={img} />)}</div>
    </div>
  </div>
);

export const CampaignSection = ({ cp, i }) => (
  <section id={cp.id} className={`scroll-mt-24 py-20 sm:py-28 ${i % 2 ? "bg-white" : "bg-[#F5F0FF]"}`} data-testid={`cs-campaign-${i + 1}`}>
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead eyebrow={cp.label ? `${cp.label} · ${cp.name}` : cp.name} title={cp.title} />
      <Reveal className="mt-12 grid gap-10 lg:grid-cols-2">
        <Block title="The challenge" paras={cp.challenge} />
        <Block title="Our approach" paras={cp.approach} />
      </Reveal>
      <Reveal className="mt-14">
        <h3 className="text-lg font-extrabold text-[#13052A] sm:text-xl">{cp.creatives ? "Campaign results" : "Results"}</h3>
        <dl className={`mt-5 grid gap-px overflow-hidden rounded-[24px] border border-violet-200 bg-violet-200 ${list(cp.results).length > 8 ? "grid-cols-2 lg:grid-cols-5" : list(cp.results).length > 4 ? "grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-3"}`} data-testid={`cs-campaign-results-${i + 1}`}>
          {list(cp.results).map((r) => (
            <div key={r.label} className="flex flex-col-reverse bg-white p-5 sm:p-6">
              <dt className="mt-1 text-sm font-semibold text-slate-600">{r.label}</dt>
              <dd className="text-2xl font-extrabold tracking-tight text-[#13052A] sm:text-4xl">{r.value}</dd>
            </div>
          ))}
        </dl>
      </Reveal>
      <Reveal className="mt-10 max-w-3xl">
        {!cp.creatives && <h3 className="text-lg font-extrabold text-[#13052A] sm:text-xl">Outcome</h3>}
        <div className={`${cp.creatives ? "" : "mt-3"} space-y-3 text-base leading-relaxed text-slate-600 md:text-lg`}>{list(cp.explanation).map((p) => <p key={p}>{p}</p>)}</div>
        {cp.note && <p className="mt-5 border-l-2 border-[#7C3AED] pl-4 text-sm leading-relaxed text-slate-600" data-testid="cs-roi-note">{cp.note}</p>}
      </Reveal>
      {cp.spotlight && <Spotlight cp={cp} />}
    </div>
  </section>
);

export const CampaignStrategy = ({ c }) => (
  <section className="grain relative overflow-hidden bg-[#13052A] py-20 text-white sm:py-28" data-testid="cs-strategy">
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead dark eyebrow="Our advertising strategy" title="More Than Just Running Ads" />
      <ol className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {list(c.strategy).map((s, i) => (
          <Reveal key={s.title} delay={i * 0.05} y={14}>
            <li className="border-t border-white/15 pt-6" data-testid={`cs-strategy-${i + 1}`}>
              <span className="serif-accent text-4xl text-[#C4B5FD]">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-3 text-lg font-extrabold">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-violet-100">{s.text}</p>
            </li>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

export const CampaignWhy = ({ c }) => (
  <section className="bg-white py-20 sm:py-28" data-testid="cs-why">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_1.4fr]">
      <SectionHead eyebrow="Why the results matter" title={c.why_title} />
      <Reveal className="space-y-5">
        {list(c.why).map((p, i) => (
          <p key={p} className={i === 0 ? "serif-accent text-2xl leading-snug text-[#581C87] md:text-3xl" : "text-base leading-relaxed text-slate-600 md:text-lg"}>{p}</p>
        ))}
      </Reveal>
    </div>
  </section>
);
